<template>
  <div class="container-fluid" style="height: 90vh">
    <div ref="mobileCtaRef" class="mobileCTA d-flex justify-content-center align-items-center p-3">
      <div class="text-center d-flex flex-column align-items-center justify-content-center" id="app-cta-overlay">
        <img class="rounded mb-3" alt="Hiiker app" width="250" src="../../images/app-hero-demo.webp"> <h3><strong>Printing from your mobile device?</strong></h3>
        <p><small>Get the best experience on the the HiiKER App</small></p>
        <a class="btn btn-primary mt-2" href="/app">
          <span>Open in app</span>
        </a>
      </div>
    </div>

    <div class="row" style="height: 90vh;">
      <div class="col-12 col-sm-9 d-flex position-relative bg-gray print-left-col">
        <img class="img-fluid home-stats-bg" src="../../images/map-lines.webp" alt="HiiKER contours" title="HiiKER contours"> <div class="map-container d-flex justify-content-center align-items-center align-self-center">
        <div class="d-flex flex-column align-items-center w-100 h-100">
          <div class="button-container">
            <div class="trail-map-buttons">
              <button class="btn btn-sm rounded-pill d-flex align-items-center" @click="buttonClicked(2)">
                <div class="d-flex align-items-center justify-content-center">
                  <span class="material-icons">search</span>
                </div>
              </button>
              <button class="btn btn-sm rounded-pill d-flex align-items-center" @click="buttonClicked(0)">
                <div class="d-flex align-items-center justify-content-center pr-1">
                  <span class="material-icons">layers</span>
                </div>
                <div>
                  Maps
                </div>
              </button>
            </div>
          </div>
          <div class="map-content ratio-1x1">
            <div id="map" ref="mapContainerRef" class="map-custom-class"></div>
          </div>
          <div class="bottom-controls-container">
            <ScaleBar v-if="mapInstance" :map="mapInstance" :latitude="mapInstance?.getCenter()?.lat || 0" />
          </div>
        </div>


        <div class="modal fade" style="background-color: rgba(7,7,7,0.36);" id="waypoint-options-dialog" tabindex="-1" role="dialog" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
              <div class="modal-body">
                <WaypointDialog />
                <p class="d-flex align-items-center">
                  <button id="close_button_map_styles" class="btn btn-secondary mt-3" data-bs-dismiss="modal">CLOSE</button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
        <MapControls />
      </div>

      <div class="col-12 col-sm-3 p-3 d-flex flex-column print-right-col">
        <h6 class="mb-2"><strong>Title</strong></h6>
        <div class="form-group">
          <input type="text" v-model="mapTitle" placeholder="Enter map title" class="form-control rounded-pill">
        </div>
        <hr class="my-3">

        <h6 class="mb-2"><strong>Page Size</strong></h6>
        <div class="btn-group w-100 mb-3" role="group">
          <button
            class="btn btn-outline-primary d-flex justify-content-center btn-sm"
            :class="{ 'active': pageSize === 'a4' }"
            @click="setPageSize('a4')"
          >A4</button>
          <button
            class="btn btn-outline-primary d-flex justify-content-center btn-sm"
            :class="{ 'active': pageSize === 'a3' }"
            @click="setPageSize('a3')"
          >A3</button>
        </div>

        <h6 class="mb-2"><strong>Scale</strong></h6>
        <div class="scale-buttons d-flex gap-2 flex-wrap text-center" role="group">
          <button
            v-for="s in scaleOptions" :key="s.value"
            class="btn btn-outline-primary btn-sm"
            :class="{'active': scale === s.value}"
            @click="setMapScale(s.value)"
          >{{ s.label }}</button>
        </div>

        <h6 class="mb-2 mt-3"><strong>Orientation</strong></h6>
        <div class="btn-group w-100" role="group">
          <button
            class="btn btn-outline-primary text-center btn-sm d-flex justify-content-center mb-2"
            :class="{ 'active': orientation === 'portrait' }"
            @click="setOrientation('portrait')"
          >Portrait</button>
          <button
            class="btn btn-outline-primary text-center btn-sm d-flex justify-content-center mb-2"
            :class="{ 'active': orientation === 'landscape' }"
            @click="setOrientation('landscape')"
          >Landscape</button>
        </div>

        <div style="margin-top: auto">
          <button class="btn btn-primary w-100 mb-4 d-flex justify-content-center" @click="printMapInfo($event)">PRINT MAP</button>
        </div>
      </div>
      <Teleport to="body">
        <BaseMapStylesDialog />
        <GoToLocationDialog />
        <LoadingDialog :show-loading-dialog="loading"/>
      </Teleport>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, nextTick } from 'vue';
import { useStore } from 'vuex';
import mapboxgl, { Map, LngLatLike, LngLatBoundsLike, Style } from 'mapbox-gl';
import type { GeoJSONSource, AnyLayer } from 'mapbox-gl';

// Import Components (Ensure paths are correct for your Vue 3 project structure)
import MapControls from "../../apps/components/MapControls.vue"; // Adjusted path
import GoToLocationDialog from "../../apps/go_to_location_dialog/GoToLocationDialog.vue"; // Adjusted path
import LoadingDialog from "../../utils/shared_components/LoadingDialog.vue";
import BaseMapStylesDialog from "../../apps/./base_map_dialog/BaseMapStylesDialog.vue"; // Adjusted path
import WaypointDialog from "../../apps/waypoint_dialog/WaypointDialog.vue"; // Adjusted path
import ScaleBar from "../../apps/components/ScaleBar.vue";

// Import Utilities
import { RevenueCatWrapper } from "../../utils/RevenueCatWrapper";
import { HiikerMapStyle, SavedCenter } from "../../utils/models/MapStyles";
import { OnBoardingGoal } from "../../utils/models/OnBoardingGoal";
import getQueryParams from "../../utils/helpers/GetQueryParamsHelper";
import getAccurateRectangle from "../../utils/helpers/getAccurateRectangle.js";
import { Modal } from 'bootstrap';
import {LayerRegistry} from "../../utils/layer-registry";
import emitter from "../../utils/bus";

// --- Types ---
// Define window interface augmentation for map and jQuery
// Do this in a global .d.ts file (e.g., src/types/shims-window.d.ts) for better practice:
/*
declare global {
  interface Window {
    map?: mapboxgl.Map | null; // Optional map instance
    $: any; // Or import types for jQuery: import $ from 'jquery'; declare global { interface Window { $: typeof $; } }
    featureCollection?: any; // Or a more specific GeoJSON type
    onBoardingVue?: { launchFor: (goal: any) => void }; // Type for onboarding if needed
  }
}
export {}; // Make it a module
*/

// --- Store ---
const store = useStore();

// --- Refs ---
const mapContainerRef = ref<HTMLDivElement | null>(null);
const mobileCtaRef = ref<HTMLDivElement | null>(null);
const mapInstance = ref<Map | null>(null); // Local map instance for scale bar

// --- State ---
const defaultMapTitle = (
  (window as typeof window & { defaultPrintMapTitle?: string }).defaultPrintMapTitle ?? ''
);
const scale = ref<string>("25000");
const mapTitle = ref<string>(defaultMapTitle);
const orientation = ref<"portrait" | "landscape">("portrait");
const pageSize = ref<"a4" | "a3">("a4");
const buttons = ref([ // Kept for potential future use, not directly used in template logic now
  { id: 0, name: 'Maps', icon: 'layers', enabled: false, dropIcon: true },
  { id: 2, name: 'Go to Location', icon: 'search', enabled: false, dropIcon: true },
]);
const lngLatToShow = ref<{ lng: string; lat: string } | null>(null);

// --- Computed ---
const loading = computed(() => store.state.printMap.loading);

// --- Constants ---
const scaleOptions = [
  { value: '10000', label: '1:10k' }, { value: '20000', label: '1:20k' },
  { value: '24000', label: '1:24k' }, { value: '25000', label: '1:25k' },
  { value: '50000', label: '1:50k' }, { value: '60000', label: '1:60k' },
  { value: '100000', label: '1:100k' }, { value: '250000', label: '1:250k' }
];
const scaleToZoom: { [key: string]: number } = {
  "10000": 16.34, "20000": 15.36, "24000": 15.09, "25000": 15.03,
  "50000": 14.05, "60000": 13.78, "100000": 13.04, "250000": 11.71
};
// Scale to size mappings for A4 (210mm × 297mm)
const scaleToSizeA4: { [key: string]: { width: number; height: number } } = {
  "10000": { width: 2, height: 2.74 }, "20000": { width: 4.0, height: 4.6 },
  "24000": { width: 4.75, height: 5.3 }, "25000": { width: 5, height: 5.5 },
  "50000": { width: 10, height: 13 }, "60000": { width: 12, height: 15 },
  "100000": { width: 19.75, height: 25.5 }, "250000": { width: 51.5, height: 63.5 }
};

// Scale to size mappings for A3 (297mm × 420mm) - approximately 1.41x larger
const scaleToSizeA3: { [key: string]: { width: number; height: number } } = {
  "10000": { width: 2.82, height: 3.87 }, "20000": { width: 5.64, height: 6.49 },
  "24000": { width: 6.70, height: 7.48 }, "25000": { width: 7.05, height: 7.76 },
  "50000": { width: 14.1, height: 18.33 }, "60000": { width: 16.92, height: 21.15 },
  "100000": { width: 27.86, height: 35.96 }, "250000": { width: 72.62, height: 89.54 }
};

// --- Methods ---

const getZoomForScale = (scaleValue: string): number | undefined => {
  return scaleToZoom[scaleValue];
};

const getRectangleSize = (scaleValue: string): { width: number; height: number } => {
  const sizeMap = pageSize.value === 'a3' ? scaleToSizeA3 : scaleToSizeA4;
  const defaultSize = pageSize.value === 'a3'
    ? { width: 70.5, height: 96.6 }  // A3 default
    : { width: 50, height: 68.5 };    // A4 default

  let size = sizeMap[scaleValue] || defaultSize;
  if (orientation.value === "landscape") {
    size = { width: size.height, height: size.width };
  }
  return size;
};

// Uses window.map
const getRectangleForScale = (): GeoJSON.Feature<GeoJSON.Polygon> | null => {
  if (!window.map) {
    console.warn("getRectangleForScale called before window.map is initialized.");
    return null;
  }
  const size = getRectangleSize(scale.value);
  const center = window.map.getCenter();
  const rectangleGeoJSON = getAccurateRectangle(center.lng, center.lat, size.width, size.height);
  if (rectangleGeoJSON?.geometry?.type === 'Polygon') {
    return rectangleGeoJSON as GeoJSON.Feature<GeoJSON.Polygon>;
  }
  console.error("Failed to generate valid rectangle GeoJSON");
  return null;
};

// Uses window.map
const updateRectangle = () => {
  if (!window.map) {
    console.warn("updateRectangle called before window.map is initialized or style is loaded.");
    return;
  }
  const rectangle = getRectangleForScale();
  if (!rectangle) {
    console.warn("No rectangle geometry available to update on the map.");
    return;
  }
  const source = window.map.getSource("rectangle-overlay") as GeoJSONSource;
  if (source) {
    source.setData(rectangle);
  } else {
    try {
      window.map.addSource("rectangle-overlay", { type: "geojson", data: rectangle });
      window.map.addLayer({
        id: "rectangle-layer", type: "line", source: "rectangle-overlay", layout: {},
        paint: { "line-color": "#FF0000", "line-width": 4, "line-dasharray": [4, 2] }
      } as AnyLayer);
    } catch (error) {
      console.error("Error adding rectangle source/layer:", error);
      // Avoid crashing if source/layer somehow already exists from rapid events
      if (window.map.getLayer("rectangle-layer")) window.map.removeLayer("rectangle-layer");
      if (window.map.getSource("rectangle-overlay")) window.map.removeSource("rectangle-overlay");
    }
  }
};

// Uses window.map
const fitMapToRectangle = () => {
  if (!window.map) {
    console.log("fitMapToRectangle called before window.map is initialized.");
    return;
  }
  const rectangle = getRectangleForScale();
  if (!rectangle?.geometry?.coordinates) {
    console.warn("No rectangle geometry available to fit map to.");
    return;
  }
  const coords = rectangle.geometry.coordinates[0];
  if (!coords || coords.length < 4)  {
    console.warn("Invalid rectangle coordinates for fitting map:", coords);
    return;
  }
  const bounds = coords.reduce<mapboxgl.LngLatBounds>((bounds, coord) => {
    return bounds.extend(coord as LngLatLike);
  }, new mapboxgl.LngLatBounds(coords[0] as LngLatLike, coords[0] as LngLatLike));
  try {
    window.map.fitBounds(bounds, { padding: 40, duration: 250 }); // Added slight duration
  } catch (e) {
    console.error("Error fitting map to bounds:", e);
  }
};

const setMapScale = (newScale: string) => {
  scale.value = newScale;
  nextTick(() => { // Wait for potential DOM updates if needed
    updateRectangle();
    fitMapToRectangle();
  });
};

const setOrientation = (newOrientation: "portrait" | "landscape") => {
  orientation.value = newOrientation;
  nextTick(() => {
    updateRectangle();
    fitMapToRectangle();
  });
};

const setPageSize = (newPageSize: "a4" | "a3") => {
  pageSize.value = newPageSize;
  nextTick(() => {
    updateRectangle();
    fitMapToRectangle();
  });
};

// Uses jQuery '$' for modals, assuming it's globally available
const buttonClicked = async (id: number) => {
  switch (id) {
    case 0: // Map Layers
      if (window.user) {
        const mapStylesDialogElement = document.getElementById("map-styles-dialog");
        const modal = new Modal(mapStylesDialogElement)
        modal.show()
        // window.$('#map-styles-dialog').modal('show');
      } else {
        console.log("User not logged in, launching onboarding for Map Styles");
        window.onBoardingVue?.launchFor(OnBoardingGoal.MapStyles) // Adapt if onboarding exists
      }
      break;
    case 2: // Go To Location
      const goToLocationDialog = document.getElementById("go-to-location-dialog");
      const modal = new Modal(goToLocationDialog)
      modal.show()
      break;
  }
};

// Uses window.map
const printMapInfo = async (event?: Event) => {
  event?.preventDefault();
  if (!window.map) {
    console.error("Map (window.map) not initialized for printing.");
    return;
  }

  const isPro = await RevenueCatWrapper.isProOrProPlus();
  if (!isPro) {
    window.location.href = '/pricing'; // Or use Vue router if applicable
    return;
  }

  store.commit('printMap/setLoading', true);

  const center = window.map.getCenter();
  const centerWkt = `POINT (${center.lng} ${center.lat})`;
  const zoomLevel = getZoomForScale(scale.value);

  if (zoomLevel === undefined) {
    console.error(`Invalid scale selected: ${scale.value}`);
    store.commit('printMap/setLoading', false);
    return;
  }

  // Calculate bounds from the rectangle overlay
  const rectangle = getRectangleForScale();
  if (!rectangle?.geometry?.coordinates) {
    console.error("Cannot get rectangle bounds for printing.");
    store.commit('printMap/setLoading', false);
    return;
  }
  const rectCoords = rectangle.geometry.coordinates[0];
  const rectNe = { lng: -Infinity, lat: -Infinity };
  const rectSw = { lng: Infinity, lat: Infinity };
  rectCoords.forEach(coord => {
    rectNe.lng = Math.max(rectNe.lng, coord[0]);
    rectNe.lat = Math.max(rectNe.lat, coord[1]);
    rectSw.lng = Math.min(rectSw.lng, coord[0]);
    rectSw.lat = Math.min(rectSw.lat, coord[1]);
  });
  const printBoundsWkt = `MULTIPOLYGON (((${rectNe.lng} ${rectNe.lat}, ${rectNe.lng} ${rectSw.lat}, ${rectSw.lng} ${rectSw.lat}, ${rectSw.lng} ${rectNe.lat}, ${rectNe.lng} ${rectNe.lat})))`; // Corrected typo sw/ne

  try {
    const fallbackTitle = defaultMapTitle || 'My HiiKER Map';
    const resolvedTitle = mapTitle.value && mapTitle.value.trim() ? mapTitle.value : fallbackTitle;

    await store.dispatch('printMap/createPrintMap', {
      bounds: printBoundsWkt,
      title: resolvedTitle,
      center: centerWkt,
      zoom: zoomLevel,
      orientation: orientation.value,
      pageSize: pageSize.value,
    });
  } catch (error) {
    console.error("Error creating print map:", error);
    store.commit('printMap/setLoading', false);
  }
  // Note: Loading state might be handled within the action/store
};

const handleMapStyleLoaded = async () => {
  updateRectangle();
  const featureCollection = store.state.printMap.featureCollection;
  if (featureCollection) {
    await store.dispatch('printMap/addFeatureCollection', {
      featureCollection,
      fitBounds: false,
    });
  }
};


// --- Lifecycle Hooks ---
onMounted(async () => {
  console.log('PrintMaps component mounted (Vue 3)');

  // Initial Store Setup
  store.commit('mapStyles/setPrintMapMode', true);
  await store.dispatch('mapStyles/initialiseStore');

  // Mobile CTA Check
  if (window.innerWidth < 600 && mobileCtaRef.value) {
    mobileCtaRef.value.style.visibility = 'visible';
  }

  // Query Params Check
  const queryParams = getQueryParams(window.location.href);
  const parseNumericParam = (value: unknown): number | undefined => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : undefined;
    }
    if (typeof value === 'string') {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
  };

  let initialCenter: LngLatLike | undefined;
  let initialZoom: number | undefined;

  const lngFromParams = parseNumericParam(queryParams.lng);
  const latFromParams = parseNumericParam(queryParams.lat);
  const zoomFromParams = parseNumericParam(queryParams.zoom);

  if (lngFromParams !== undefined && latFromParams !== undefined) {
    initialCenter = [lngFromParams, latFromParams];
    console.log("Found lng/lat in query params:", initialCenter);
  }

  if (zoomFromParams !== undefined) {
    initialZoom = zoomFromParams;
    console.log("Found zoom in query params:", initialZoom);
  } else if (initialCenter) {
    // Use a sensible default zoom when center is provided but zoom is missing.
    initialZoom = 14;
  }

  // Map Initialization
  if (!mapContainerRef.value) {
    console.error("Map container element not found.");
    return;
  }

  const storeInitialSettings = store.getters['printMap/initialMapSettings'];
  const mapOptions = {
    container: mapContainerRef.value,
    ...storeInitialSettings,
    center: initialCenter || storeInitialSettings.center, // Use query param center if available
    zoom: initialZoom || storeInitialSettings.zoom,       // Use query param zoom if available
    interactive: true
  };
  console.log("Map options:", mapOptions);

  // Assign to window.map
  window.map = new Map(mapOptions);
  mapInstance.value = window.map; // Store instance locally for scale bar
  window.layerRegistry = new LayerRegistry(window.map); // Assuming LayerRegistry is defined elsewhere

  // Attach Map Event Listeners
  window.map.on('load', async () => {
    console.log('window.map loaded event');
    await store.dispatch('waypoints/initialiseStore'); // Init waypoints after load

    // Create geolocate control for MapControls to use, but don't add it to the map
    // to avoid showing the native Mapbox location button
    const geolocate = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserLocation: true,
      showAccuracyCircle: true,
    });

    geolocate.on("geolocate", () => window.geolocateLocated?.());
    geolocate.on("error", (error) => {
      console.error("Error getting location:", error);
      window.geolocateError?.();
    });

    geolocate.permissionAndTrigger = () => {
      if ("geolocation" in navigator) {
        navigator.permissions.query({ name: "geolocation" }).then((result) => {
          if (result.state === "granted") {
            geolocate.trigger();
          } else if (result.state === "prompt") {
            navigator.geolocation.getCurrentPosition(
              () => geolocate.trigger(),
              () => {
                console.error("User denied permission to access location");
                alert("User denied permission to access location");
                window.geolocateError?.();
              }
            );
          } else {
            console.error("User denied permission to access location");
            alert("Please enable location services to use this feature");
            window.geolocateError?.();
          }
        }).catch((error) => {
          console.error("Permission query failed:", error);
          window.geolocateError?.();
          alert("Failed to check location permissions: " + error.message);
        });
      } else {
        alert("Geolocation is not available on your device. Please contact support@hiiker.co");
        console.error("Geolocation is not available");
        window.geolocateError?.();
      }
    };

    // Add control to map so it can function properly
    window.map.addControl(geolocate);

    // Expose globally for MapControls to use
    window.geoLocate = geolocate;

    // Hide the native button immediately and persistently
    const hideButton = () => {
      const element = document.querySelector(".mapboxgl-ctrl-geolocate");
      if (element instanceof HTMLElement) {
        element.style.display = "none";
      }
    };

    // Hide on load and after each style change
    hideButton();
    setTimeout(hideButton, 100);

    setMapScale(scale.value); // Set initial scale/rectangle/fit

    if (window.featureCollection) { // Add feature collection if present globally
      console.log("Adding feature collection from window");
      setTimeout(async () => { // Keep potential delay
        await store.dispatch('printMap/addFeatureCollection', {
          featureCollection: window.featureCollection,
          fitBounds: true,
        });
      }, 500);
    }

    await store.dispatch('printMap/mapInitialised'); // Signal map init completed
    console.log("window.map fully initialised.");
  });

  window.map.on('style.load', () => {
    console.log('window.map style loaded event');
    setTimeout(updateRectangle, 100); // Update rectangle on style change

    // Hide geolocate button after style change
    setTimeout(() => {
      const element = document.querySelector(".mapboxgl-ctrl-geolocate");
      if (element instanceof HTMLElement) {
        element.style.display = "none";
      }
    }, 100);
  });

  window.map.on('moveend', () => {
    console.log('window.map move ended');
    if (window.map) { // Check if map exists before using
      SavedCenter.saveCurrentCenter(window.map); // Pass map instance
      updateRectangle(); // Update rectangle position
    }
  });

  window.map.on('zoomend', () => {
    console.log('window.map zoom ended');
    updateRectangle(); // Update rectangle on zoom change
  });

  window.map.on('error', (e) => { console.error('Mapbox error:', e); });

  // --- Set Initial Style ---
  const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
  let currentStyle = HiikerMapStyle.getCurrentStyle(isProOrProPlus);
  if (currentStyle.name === HiikerMapStyle.hiking().name) { // Force Standard if Hiking
    currentStyle = HiikerMapStyle.standard();
    currentStyle.saveCurrentStyle();
    store.commit('mapStyles/setCurrentMapStyle', currentStyle);
  }
  const styleUrl = currentStyle.printMapUrl();
  console.log("Setting initial map style:", styleUrl);
  window.map.setStyle(styleUrl); // Set style on window.map

  emitter.on('map-style-loaded', handleMapStyleLoaded);
});

onBeforeUnmount(() => {
  console.log('PrintMaps component unmounting');
  emitter.off('map-style-loaded', handleMapStyleLoaded);
  // Clean up window.map
  if (window.map) {
    window.map.remove();
    window.map = null; // Remove reference
  }
  // Reset relevant store state
  store.commit('mapStyles/setPrintMapMode', false);
  // store.commit('printMap/resetState'); // Consider adding a reset mutation if needed
});

</script>

// --- Style Section (Unchanged from your last request) ---
<style scoped>
.print-left-col {
  max-height: 90vh;
  position: relative;
  height: 100%;
}

.home-stats-bg {
  z-index: 0;
}

.map-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.map-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 0;
  overflow: hidden;
}

.map-custom-class, canvas {
  width: 100%;
  height: 100%;
}

.button-container {
  position: absolute;
  top: 60px;
  right: 10px;
  z-index: 10;
  display: flex;
  gap: 10px;
}

.mapboxgl-ctrl-scale {
  border-radius: 0 0 10px 10px;
  position: absolute;
  top: 59px;
  left: 5px;
  padding: 5px;
  background-color: #fff;
  border: 1px solid #ccc;
  font-size: 12px;
  line-height: 1.5;
  z-index: 1000;
}

.mobileCTA {
  position: absolute;
  z-index: 10000;
  background: #202020fa;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  visibility: hidden;
}
</style>
